import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { pulizieApi, erroreApi } from './api';
import { FasceZone } from './FasceZone';
import { TurnoOperatore } from './TurnoOperatore';

const MEMORIA = 'pulizie_compila_ultime';
const ieri = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const campo = 'h-11 w-full px-3 rounded-lg border border-gray-300 bg-white';

const iniziale = (operatore, zone) => {
  const m = JSON.parse(localStorage.getItem(MEMORIA) || '{}');
  const fasce = Object.fromEntries(zone.map((z) => [z, m.fasce?.[z] || { da: '22:00', a: '23:30' }]));
  return { da: '', a: ieri(), operatore: operatore || '', giorni_liberi: m.giorni_liberi || [], sostituto: m.sostituto || '', zone, fasce };
};

const Riepilogo = ({ r }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="pulizie-compila-anteprima">
    <p className="font-semibold text-slate-900">{r.registrazioni ? `${r.registrazioni} pulizie in ${r.giorni} giorni` : 'Niente da compilare in questo periodo'}</p>
    {Object.entries(r.per_zona).map(([z, n]) => <p key={z} className="text-slate-600">{z}: {n}</p>)}
    {Object.keys(r.per_operatore || {}).length > 0 && (
      <p className="mt-1 text-slate-700" data-testid="pulizie-compila-per-operatore">
        {Object.entries(r.per_operatore).map(([o, n]) => `${o}: ${n}`).join(' · ')}
      </p>
    )}
    <p className="mt-1 text-xs text-slate-500">Saltati: {r.giorni_chiusura} giorni di chiusura, {r.gia_registrate} zone già registrate.</p>
  </div>
);

export const CompilaPeriodoDialog = ({ open, operatore, zone, onClose, onFatto }) => {
  const [f, setF] = useState(() => iniziale(operatore, zone));
  const [nomi, setNomi] = useState([]);
  const [anteprima, setAnteprima] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(iniziale(operatore, zone)); setAnteprima(null);
    pulizieApi.get('/operatori').then((d) => setNomi(d.nomi)).catch(() => setNomi([]));
  }, [open, operatore, zone]);
  const cambia = (k, v) => { setF((x) => ({ ...x, [k]: v })); setAnteprima(null); };
  const toggleZona = (z) => cambia('zone', f.zone.includes(z) ? f.zone.filter((x) => x !== z) : [...f.zone, z]);
  const fascia = (z, v) => cambia('fasce', { ...f.fasce, [z]: v });

  const invia = async (conferma) => {
    setBusy(true);
    try {
      const fasce = Object.fromEntries(f.zone.map((z) => [z, f.fasce[z]]));
      const r = await pulizieApi.post('/compila-periodo', { ...f, fasce, conferma });
      if (!conferma) { setAnteprima(r); return; }
      localStorage.setItem(MEMORIA, JSON.stringify({ fasce: f.fasce, giorni_liberi: f.giorni_liberi, sostituto: f.sostituto }));
      onFatto(r);
    } catch (e) {
      toast.error(erroreApi(e, 'Compilazione non riuscita'));
    } finally {
      setBusy(false);
    }
  };
  const pronto = f.da && f.a && f.da <= f.a && f.operatore.trim().length >= 2 && f.zone.length
    && (!f.giorni_liberi.length || f.sostituto.trim().length >= 2);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-1rem)] rounded-2xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="pulizie-compila-dialog">
        <DialogHeader>
          <DialogTitle>Compila da data a data</DialogTitle>
          <DialogDescription>Riempio solo i giorni passati, salto chiusure e zone già registrate, seguo le frequenze del piano.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs font-medium">Dal<input type="date" value={f.da} max={f.a || ieri()} onChange={(e) => cambia('da', e.target.value)} className={campo} data-testid="pulizie-compila-da" /></label>
          <label className="text-xs font-medium">Al<input type="date" value={f.a} min={f.da} max={ieri()} onChange={(e) => cambia('a', e.target.value)} className={campo} data-testid="pulizie-compila-a" /></label>
        </div>
        <TurnoOperatore f={f} nomi={nomi} onCambia={cambia} />
        <FasceZone zone={zone} scelte={f.zone} fasce={f.fasce} onToggle={toggleZona} onFascia={fascia} />
        {anteprima && <Riepilogo r={anteprima} />}
        {anteprima?.registrazioni ? (
          <Button onClick={() => invia(true)} disabled={busy} className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pulizie-compila-conferma">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Conferma: inserisci ${anteprima.registrazioni} pulizie`}
          </Button>
        ) : (
          <Button onClick={() => invia(false)} disabled={!pronto || busy} className="h-11 bg-slate-900 hover:bg-slate-800 text-white" data-testid="pulizie-compila-calcola">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Calcola'}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
